// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// verify.ts — is tonight's building actually robbable, and what is the best anybody could do on it?
//
// IT ANSWERS BOTH IN ONE PASS, WHICH IS THE POINT. Par is not a theoretical maximum computed from the
// loot table and it is not a number walked backwards from the goal: it is the score of a route this
// search ACTUALLY FOUND through this actual floor, produced by the same search that decides whether
// the seed is playable at all. So the summary screen can say "par took nineteen turns and four held
// breaths" instead of "you got forty-five per cent of theoretical", and there is exactly ONE number
// called par — the same one the game scores against.
//
// THE DESIGN'S GATE WAS ARITHMETICALLY IMPOSSIBLE. It asked for "a five-case route with heat pinned
// CALM", but five grabs is thirty heat, which is RESTLESS by construction — so every seed would have
// hard-failed, or the gate would have been quietly relaxed until it meant nothing. This one asks a
// question that can be answered: what is the best route that is NEVER PERCEIVED, given a pessimistic
// model of what taking cases costs you.
//
// PESSIMISTIC ON PURPOSE, SO PAR IS ACHIEVABLE RATHER THAN ASPIRATIONAL:
//   • every case taken is assumed to be NOTICED — HEAT_GRAB for the taking plus HEAT_READ for the
//     mark, so the band widens faster in here than it usually will in a real run;
//   • crossing a door costs an extra slot EVERY time, as though it had to be opened afresh;
//   • the route is checked for perception at EVERY SLOT, not at the end of each turn.
// A player who closes doors behind them and outruns the reads will beat par, and that is the correct
// direction for a par to be wrong in. A par nobody can reach is a scoreboard that lies.
//
// This module receives the finished grid and the placed bodies. It does NOT import gen.ts and knows
// nothing about the room tree, the BSP or the reroll loop that produced them — the generator's own
// claim that a floor is solvable is never the thing that decides.

import {
  BASE_RANGE,
  Band,
  CELLS,
  CYCLOMATIC_MIN,
  FORCED_RISK_MIN,
  ARC_COVERAGE_MAX,
  ARC_COVERAGE_MIN,
  Floor,
  GHOST_PCT,
  HEAT_GRAB,
  HEAT_READ,
  TIME_CAP,
  TIME_PCT,
  LENS_PERIOD,
  LENS_RANGE,
  MINDER_STEPS,
  NON_CARPET_MIN_PCT,
  QUIET_PCT,
  SLACK_MAX_PCT,
  SLACK_MIN_PCT,
  SLOTS,
  VERB_SLOTS,
  Verb,
  WORLD_PERIOD,
  BAND_FLOOR,
} from './tuning';
import { canStep, chebyshev, facingOf, hasLos, inArc, neighbours, walkField, type Door, type Grid } from './map';
import type { Building } from './rules';
import type { Mode } from './modes';

export interface Verdict {
  ok: boolean;
  /** Why it was rejected, for the generator's log and the gen test's failure message. */
  reason: string;
  /** The score of the route below. THE one par. */
  par: number;
  /** Cells of the par route, in order — drawn beside the player's own on the summary card. */
  route: number[];
  /** Turns the par route consumed. */
  parTurns: number;
  /** Item ids the par route took. */
  parItems: number[];
  /** Par-route cells that lie inside an arc at SOME phase: the hold-your-breath moments. */
  forcedRisk: number;
  arcCoverage: number;
  cyclomatic: number;
  nonCarpetPct: number;
}

export const popcount = (n: number): number => {
  let c = 0;
  while (n) {
    n &= n - 1;
    c++;
  }
  return c;
};

/** How many steps every minder has taken by global slot `t`. Two a turn, at slots 1 and 4. */
function stepsAt(t: number): number {
  const turn = Math.floor(t / SLOTS);
  const slot = t % SLOTS;
  let n = turn * MINDER_STEPS.length;
  for (const ms of MINDER_STEPS) if (slot >= ms) n++;
  return n;
}

/**
 * Where a patrolling body stands after `steps` steps.
 *
 * This is exact rather than approximate because gen.ts emits CLOSED circuits whose consecutive cells
 * are adjacent and whose last cell is adjacent to the first: a patrol therefore has period
 * `loop.length` in steps, with no pathing back to the start to model. That property is the whole
 * reason the loop lengths are constrained to {4,6,8,12}.
 */
const patrolAt = (start: number, loop: readonly number[], steps: number): number =>
  steps <= 0 ? start : loop[(steps - 1) % loop.length];

function patrolFacing(start: number, loop: readonly number[], steps: number): number {
  if (steps <= 0) return loop.length ? facingOf(start, loop[0]) : 0;
  const cur = patrolAt(start, loop, steps);
  const prev = patrolAt(start, loop, steps - 1);
  return prev === cur ? facingOf(start, cur) : facingOf(prev, cur);
}

/** The band a route is in, pessimistically, after taking `grabs` cases. */
const bandAfter = (mode: Mode, grabs: number): Band => {
  const heat = mode.startHeat + (HEAT_GRAB + HEAT_READ) * grabs;
  let b = Band.Calm;
  for (let i = 1; i < BAND_FLOOR.length; i++) if (heat >= BAND_FLOOR[i]) b = i as Band;
  return b;
};

/**
 * A cached "is a standing intruder at `cell` perceived at global slot `t` with `grabs` cases taken?".
 *
 * THE CACHE IS WHAT MAKES THIS RUN ON A PHONE. The search visits 386,560 states and every one asks
 * this question, but the ANSWER depends only on (cell, slot, tier) — the loot bitmask enters solely
 * through the tier it implies. So the whole answer for one (slot, tier) is one 80-byte mask built once
 * and read thereafter, which turns roughly two million line-of-sight walks into a couple of hundred
 * thousand. Generation runs inside the player's first tap, so this is a correctness budget rather than
 * a nicety: the run-start card cannot wait half a second for the building it is describing.
 */
function makePercept(b: Building, mode: Mode) {
  // A FLAT typed array rather than a Map, because this is read about two and a half million times per
  // building and a Map lookup in that inner loop was measurably most of the generation budget.
  const slots = mode.turns * SLOTS + 1;
  const flat = new Uint8Array(slots * 4 * CELLS);
  const done = new Uint8Array(slots * 4);
  const nMinders = Math.min(mode.minders, b.minderStarts.length);
  const nLenses = Math.min(mode.lenses, b.lensStarts.length);
  // Brownout is gloom everywhere, which shortens every arc. The search is entitled to that because it
  // is a property of the mode rather than of anything the route did.
  const gloom = mode.mainsOut ? 2 : 0;

  const build = (t: number, tier: Band, base: number): void => {
    const wide = tier >= Band.Sweep;
    const range = Math.max(1, BASE_RANGE[tier] - gloom);
    const steps = stepsAt(t);
    const turn = Math.floor(t / SLOTS);
    const eyes: { cell: number; facing: number; range: number; wide: boolean }[] = [];
    for (let i = 0; i < nMinders; i++) {
      const m = b.minderStarts[i];
      eyes.push({
        cell: patrolAt(m.cell, m.loop, steps),
        facing: patrolFacing(m.cell, m.loop, steps),
        range,
        wide,
      });
    }
    for (let i = 0; i < nLenses; i++) {
      const l = b.lensStarts[i];
      eyes.push({ cell: l.cell, facing: (l.facing0 + turn) % LENS_PERIOD & 7, range: LENS_RANGE, wide: true });
    }
    for (let c = 0; c < CELLS; c++) {
      if (b.grid.wall[c]) continue;
      for (const e of eyes) {
        if (chebyshev(e.cell, c) > e.range) continue;
        if (!inArc(e.cell, e.facing, c, e.wide)) continue;
        if (!hasLos(b.grid, b.doors, e.cell, c)) continue;
        flat[base + c] = 1;
        break;
      }
    }
  };

  /**
   * The raw tables are exposed alongside the friendly accessor so the hot loop can index them
   * directly. A helper call that re-derives the band from a grab count on every one of two and a
   * half million transitions is not a helper, it is the budget.
   */
  return {
    flat,
    done,
    /** Make sure the mask for this (slot, tier) exists. `tier` must already be resolved. */
    ensure(t: number, tier: Band): void {
      const key = t * 4 + tier;
      if (done[key]) return;
      done[key] = 1;
      build(t, tier, key * CELLS);
    },
    test(cell: number, t: number, grabs: number): boolean {
      const tier = bandAfter(mode, grabs);
      this.ensure(t, tier);
      return flat[(t * 4 + tier) * CELLS + cell] === 1;
    },
  };
}

export type Percept = ReturnType<typeof makePercept>;

/**
 * The best never-perceived route through this building, by forward DP over
 * (cell, global slot, taken-item bitmask).
 *
 * Every transition strictly increases the slot index, so the graph is a DAG and one forward pass in
 * slot order is exact — no priority queue and no relaxation. 80 cells x 151 slots x 32 masks is
 * 386,560 states, which is nothing, and it is why the state can afford to carry the loot bitmask that
 * the design's (cell, turn mod 24) formulation could not. That omission was fatal: without the mask
 * the search certified routes that no player could execute, and without per-slot checking it certified
 * routes that are perceived in the middle of a turn.
 */
function search(
  b: Building,
  mode: Mode,
  perceived: Percept,
): Omit<Verdict, 'ok' | 'reason' | 'arcCoverage' | 'cyclomatic' | 'nonCarpetPct'> {
  const items = b.items.slice(0, Math.max(1, mode.loot || 1));
  const nItems = items.length;
  const masks = 1 << nItems;
  const maxT = mode.turns * SLOTS;
  const stride = CELLS * masks;
  const seen = new Uint8Array((maxT + 1) * stride);
  const prev = new Int32Array((maxT + 1) * stride).fill(-1);

  const itemAt = new Int8Array(CELLS).fill(-1);
  for (let i = 0; i < nItems; i++) itemAt[items[i].cell] = i;

  const start = b.entry * masks;
  seen[start] = 1;
  const nb: number[] = [];

  let bestScore = 0;
  let bestNode = -1;
  let bestTurns = 0;
  let bestMask = 0;

  // Everything the inner loop needs, precomputed into flat arrays. This loop runs about two and a half
  // million times per building and generation happens inside the player's first tap, so a property
  // read or a helper call in here is not a style question.
  const bits = new Uint8Array(masks);
  for (let m = 1; m < masks; m++) bits[m] = bits[m >> 1] + (m & 1);
  const tierOf = new Uint8Array(masks);
  for (let m = 0; m < masks; m++) tierOf[m] = bandAfter(mode, bits[m]);
  const isExit = new Uint8Array(CELLS);
  for (const e of b.exits) if (e >= 0) isExit[e] = 1;
  const value = new Int16Array(masks);
  for (let m = 0; m < masks; m++)
    for (let i = 0; i < nItems; i++) if (m & (1 << i)) value[m] += items[i].value;

  // Precomputed neighbours and their slot cost, so the walk does no geometry at all.
  const nbCell = new Int16Array(CELLS * 4).fill(-1);
  const nbCost = new Uint8Array(CELLS * 4);
  for (let c = 0; c < CELLS; c++) {
    if (b.grid.wall[c]) continue;
    neighbours(c, nb);
    let k = 0;
    for (const n of nb) {
      if (b.grid.wall[n]) continue;
      nbCell[c * 4 + k] = n;
      // A door costs an extra slot every single time, as though it had to be opened afresh.
      nbCost[c * 4 + k] = b.grid.doorAt[n] >= 0 ? 2 : 1;
      k++;
    }
  }

  /**
   * Distance from every cell to the nearest exit, with all doors open. A state that cannot possibly
   * reach an exit in the slots it has left cannot contribute to par, so it is never expanded — which
   * removes most of the late-slot half of the space and is the single biggest saving here.
   */
  const openDoors: Door[] = b.doors.map((d) => ({ ...d, state: 0 }));
  const toExit = new Int16Array(CELLS).fill(32767);
  for (const e of b.exits) {
    if (e < 0) continue;
    const f = walkField(b.grid, openDoors, e);
    for (let c = 0; c < CELLS; c++) if (f[c] >= 0 && f[c] < toExit[c]) toExit[c] = f[c];
  }

  const pFlat = perceived.flat;
  const pDone = perceived.done;

  for (let t = 0; t < maxT; t++) {
    for (let c = 0; c < CELLS; c++) {
      if (b.grid.wall[c]) continue;
      const exitHere = isExit[c];
      const slack = maxT - t - toExit[c];
      if (slack < 0 && !exitHere) continue;
      const base = c * masks;
      const ai = itemAt[c];
      for (let m = 0; m < masks; m++) {
        const here = t * stride + base + m;
        if (!seen[here]) continue;

        // Reaching an exit banks whatever you are carrying, so a par route needs no chute.
        if (exitHere && m !== 0) {
          const turnsUsed = Math.ceil(t / SLOTS);
          // Never perceived by construction, so GHOST always holds; QUIET holds only while the
          // pessimistic band stayed under SWEEP.
          const left = Math.max(0, mode.turns - turnsUsed);
          const mult =
            100 + GHOST_PCT + (tierOf[m] >= Band.Sweep ? 0 : QUIET_PCT) + Math.min(TIME_CAP, left * TIME_PCT);
          const score = Math.floor((value[m] * mult) / 100);
          if (score > bestScore) {
            bestScore = score;
            bestNode = here;
            bestTurns = turnsUsed;
            bestMask = m;
          }
        }

        const tier = tierOf[m];
        // Hold. Standing still is a real move: it is how you let a patrol pass.
        if (t + 1 <= maxT) {
          const key = (t + 1) * 4 + tier;
          if (!pDone[key]) perceived.ensure(t + 1, tier);
          if (!pFlat[key * CELLS + c]) {
            const n = (t + 1) * stride + base + m;
            if (!seen[n]) {
              seen[n] = 1;
              prev[n] = here;
            }
          }
        }

        for (let k = 0; k < 4; k++) {
          const nc = nbCell[c * 4 + k];
          if (nc < 0) break;
          const nt = t + nbCost[c * 4 + k];
          if (nt > maxT) continue;
          const key = nt * 4 + tier;
          if (!pDone[key]) perceived.ensure(nt, tier);
          if (pFlat[key * CELLS + nc]) continue;
          const n = nt * stride + nc * masks + m;
          if (seen[n]) continue;
          seen[n] = 1;
          prev[n] = here;
        }

        if (ai >= 0 && !(m & (1 << ai))) {
          const nm = m | (1 << ai);
          const nt = t + VERB_SLOTS[Verb.Grab];
          if (nt <= maxT) {
            const nTier = tierOf[nm];
            const key = nt * 4 + nTier;
            if (!pDone[key]) perceived.ensure(nt, nTier);
            if (!pFlat[key * CELLS + c]) {
              const n = nt * stride + base + nm;
              if (!seen[n]) {
                seen[n] = 1;
                prev[n] = here;
              }
            }
          }
        }
      }
    }
  }

  const route: number[] = [];
  for (let n = bestNode; n >= 0; n = prev[n]) {
    const cell = Math.floor((n % stride) / masks);
    if (route.length === 0 || route[route.length - 1] !== cell) route.push(cell);
  }
  route.reverse();

  const parItems: number[] = [];
  for (let i = 0; i < nItems; i++) if (bestMask & (1 << i)) parItems.push(items[i].id);

  // A cell of the par route that lies inside an arc at SOME phase is a moment the player has to time.
  // That integer IS the difficulty of tonight's building, measured rather than asserted.
  let forcedRisk = 0;
  for (const c of route) {
    for (let ph = 0; ph < WORLD_PERIOD; ph++) {
      if (perceived.test(c, ph * SLOTS, 0)) {
        forcedRisk++;
        break;
      }
    }
  }

  return { par: bestScore, route, parTurns: bestTurns, parItems, forcedRisk };
}

/** What share of the walkable floor is watched by somebody at some point in the cycle? */
function arcCoverage(b: Building, perceived: Percept): number {
  let walk = 0;
  let seen = 0;
  for (let c = 0; c < CELLS; c++) {
    if (!canStep(b.grid, b.doors, c)) continue;
    walk++;
    for (let ph = 0; ph < WORLD_PERIOD; ph++) {
      if (perceived.test(c, ph * SLOTS, 0)) {
        seen++;
        break;
      }
    }
  }
  return walk ? Math.round((seen * 100) / walk) : 0;
}

/** E - V + 1 over the room graph. Below 2 there is a room you can only leave the way you came in. */
function cyclomatic(g: Grid, doors: readonly Door[]): number {
  const edges = new Set<string>();
  for (const d of doors) {
    const nb: number[] = [];
    neighbours(d.cell, nb);
    const rooms = [...new Set(nb.map((n) => g.room[n]).filter((r) => r >= 0))].sort();
    for (let i = 0; i < rooms.length; i++)
      for (let j = i + 1; j < rooms.length; j++) edges.add(`${rooms[i]}-${rooms[j]}`);
  }
  return edges.size - g.roomCount + 1;
}

function nonCarpet(g: Grid): number {
  let walk = 0;
  let hard = 0;
  for (let c = 0; c < CELLS; c++) {
    if (g.wall[c]) continue;
    walk++;
    if (g.floorT[c] !== Floor.Carpet) hard++;
  }
  return walk ? Math.round((hard * 100) / walk) : 0;
}

/**
 * The whole verdict. Reachability first (cheap and absolute), then the search, then the interest
 * metrics — because solvable is a floor, not a target: a building can verify perfectly and still be a
 * stroll, and a stroll is a worse thing to ship than a hard seed.
 */
export function verify(b: Building, mode: Mode): Verdict {
  const fail = (reason: string): Verdict => ({
    ok: false,
    reason,
    par: 0,
    route: [],
    parTurns: 0,
    parItems: [],
    forcedRisk: 0,
    arcCoverage: 0,
    cyclomatic: 0,
    nonCarpetPct: 0,
  });

  // (a) Flood with the SHIPPED canStep, not with a private copy of the walkability rule. Doors start
  // closed, so the flood is run with every door open — the question is whether the floor is connected,
  // not whether it is connected right this second.
  const openDoors: Door[] = b.doors.map((d) => ({ ...d, state: 0 }));
  const field = walkField(b.grid, openDoors, b.entry);
  const need: [string, number][] = [
    ...b.items.map((i) => ['a case', i.cell] as [string, number]),
    ...b.chutes.map((c) => ['a chute', c] as [string, number]),
    ...b.ducts.map((c) => ['a duct mouth', c] as [string, number]),
    ['the strongroom', b.strongroom],
  ];
  for (const [what, cell] of need) if (cell < 0 || field[cell] < 0) return fail(`${what} is unreachable`);
  if (b.exits.filter((e) => field[e] >= 0).length < 2) return fail('fewer than two reachable exits');

  const nc = nonCarpet(b.grid);
  if (nc < NON_CARPET_MIN_PCT) return fail(`only ${nc}% hard flooring — hearing would be inert`);

  const cy = cyclomatic(b.grid, b.doors);
  if (cy < CYCLOMATIC_MIN) return fail(`cyclomatic ${cy}: somewhere you can only leave the way you came`);

  const percept = makePercept(b, mode);
  const cov = arcCoverage(b, percept);
  if (cov < ARC_COVERAGE_MIN) return fail(`arc coverage ${cov}%: it is a stroll`);
  if (cov > ARC_COVERAGE_MAX) return fail(`arc coverage ${cov}%: it is a crawl`);

  const found = search(b, mode, percept);
  if (found.par <= 0 || found.parItems.length < Math.min(2, b.items.length)) {
    return fail('no never-perceived route banks two cases');
  }

  const slack = Math.round((found.parTurns * 100) / mode.turns);
  if (slack < SLACK_MIN_PCT) return fail(`par uses only ${slack}% of the clock`);
  if (slack > SLACK_MAX_PCT) return fail(`par uses ${slack}% of the clock: no room for one mistake`);

  if (found.forcedRisk < FORCED_RISK_MIN) {
    return fail(`forced risk ${found.forcedRisk}: nothing to hold your breath for`);
  }

  return { ok: true, reason: '', ...found, arcCoverage: cov, cyclomatic: cy, nonCarpetPct: nc };
}
